import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '../api/client';

interface ChapterMember {
  id: string;
  firstName: string;
  lastName: string;
  chapterAffiliation?: string;
  user?: { id: string; fullName: string } | null;
}

interface ChapterMemberSearchDropdownProps {
  value: string | null;
  onChange: (userId: string, fullName: string) => void;
  chapter?: string;
  placeholder?: string;
}

const dropdownStyles: Record<string, React.CSSProperties> = {
  container: { position: 'relative', width: '100%' },
  input: {
    padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px',
    fontSize: '0.9rem', boxSizing: 'border-box', width: '100%',
  },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '200px',
    overflowY: 'auto', backgroundColor: '#fff', border: '1px solid #ccc',
    borderTop: 'none', borderRadius: '0 0 4px 4px', zIndex: 1000,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  option: {
    padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem',
    borderBottom: '1px solid #f0f0f0',
  },
  optionHover: { backgroundColor: '#f0f7f2' },
  loading: { padding: '0.5rem 0.75rem', color: '#666', fontSize: '0.85rem', textAlign: 'center' },
  noResults: { padding: '0.5rem 0.75rem', color: '#666', fontSize: '0.85rem', textAlign: 'center' },
  chapterBadge: {
    fontSize: '0.7rem', color: '#666', marginLeft: '0.5rem', fontStyle: 'italic',
  },
};

export function ChapterMemberSearchDropdown({
  value,
  onChange,
  chapter = 'Georgia',
  placeholder = 'Search Georgia chapter members...',
}: ChapterMemberSearchDropdownProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState<ChapterMember[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedName, setSelectedName] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Resolve name when value changes externally
  useEffect(() => {
    if (!value) { setSelectedName(''); return; }
    if (selectedName) return;
    // Try to find the member name from existing options
    const found = options.find((m) => m.user?.id === value);
    if (found) setSelectedName(`${found.firstName} ${found.lastName}`);
  }, [value]);

  const fetchMembers = useCallback(async (query: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.set('search', query);
      if (chapter) params.set('chapterAffiliation', chapter);
      const members = await apiClient<ChapterMember[]>(`/members?${params.toString()}`);
      setOptions(members);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [chapter]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setSelectedName('');
    setIsOpen(true);
    setHighlightedIndex(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchMembers(query), 300);
  };

  const handleFocus = () => {
    setIsOpen(true);
    if (options.length === 0 && !loading) fetchMembers(searchQuery);
  };

  const handleSelect = (member: ChapterMember) => {
    const fullName = `${member.firstName} ${member.lastName}`;
    setSelectedName(fullName);
    setSearchQuery('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    // Use the linked user ID if available, otherwise member ID
    const id = member.user?.id || member.id;
    onChange(id, fullName);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < options.length) {
        handleSelect(options[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const displayValue = selectedName || searchQuery;

  return (
    <div ref={containerRef} style={dropdownStyles.container}>
      <input
        type="text"
        style={dropdownStyles.input}
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label="Search chapter members"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        role="combobox"
      />
      {isOpen && (
        <div style={dropdownStyles.dropdown} role="listbox">
          {/* Fixed "General" option at top */}
          <div
            style={{
              ...dropdownStyles.option,
              fontWeight: 600,
              borderBottom: '2px solid #e5e7eb',
              backgroundColor: highlightedIndex === -2 ? '#f0f7f2' : undefined,
            }}
            onClick={() => {
              setSelectedName('General');
              setSearchQuery('');
              setIsOpen(false);
              setHighlightedIndex(-1);
              onChange('general', 'General');
            }}
            onMouseEnter={() => setHighlightedIndex(-2)}
            role="option"
            aria-selected={highlightedIndex === -2}
          >
            <span>🌐 General</span>
            <span style={dropdownStyles.chapterBadge}>Sponsors all members</span>
          </div>
          {loading && <div style={dropdownStyles.loading}>Loading...</div>}
          {!loading && options.length === 0 && (
            <div style={dropdownStyles.noResults}>No members found</div>
          )}
          {!loading && options.map((member, idx) => (
            <div
              key={member.id}
              style={{
                ...dropdownStyles.option,
                ...(idx === highlightedIndex ? dropdownStyles.optionHover : {}),
              }}
              onClick={() => handleSelect(member)}
              onMouseEnter={() => setHighlightedIndex(idx)}
              role="option"
              aria-selected={idx === highlightedIndex}
            >
              <span style={{ fontWeight: 500 }}>{member.firstName} {member.lastName}</span>
              {member.chapterAffiliation && (
                <span style={dropdownStyles.chapterBadge}>{member.chapterAffiliation}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
